// 261005 E2-P2b — "ý định gửi khung": ⑤ (dòng Cần xem / nút đầu tập) nhờ khung Trợ lý nói về một tập + tick lỗi + điền câu gợi ý.
// Hàm thuần, KHÔNG import antd/react (jest). Khung KHÔNG tự gửi — chỉ chuẩn bị ô soạn.
import type { EpisodeScriptRecord } from '../../../../types/adaptation';
import type { ScopeView } from '../../../../types/adaptationAssistant';
import { editBlockerText } from '../../episodeFixUtils';
import type { ReviewReason } from '../scriptStageView';
import type { AssistantScope } from './assistantView';

/** Người gọi KHÔNG truyền nonce — vỏ tự sinh. */
export interface AssistantRequest {
  /** ⑤: số tập. ② (cả truyện): bỏ trống. */
  episode?: number;
  /** Thiếu ⇒ `'script'` (caller cũ của ⑤ không đổi). */
  stageKey?: 'script' | 'skeleton' | 'split' | 'source' | 'localize';
  issueIds: string[];
  /** ② : vi phạm FE đang hiển thị — KHUNG tra id chip BE (`resolveSkeletonIssueIds`), FE không tự tính id. Thêm vào `issueIds` đã tra được. */
  issueRefs?: Array<{ gate: string; refs: string[]; message?: string }>;
  suggestedText?: string; openVersions?: boolean;
  /** 261005 E3-P4b1 — "Đưa ra phương án" (①): sau khi tick chip, khung TỰ GỬI chế độ 3 phương án đúng MỘT lần (xem `autoSendDecision`). */
  /** `fix` (261015 P5a D1) = "Nhờ AI sửa (tốn AI)": gửi `mode:'fix'` + `FIX_REQUEST_TEXT`; BE tự áp khi an toàn. */
  autoSend?: 'options' | 'fix';
}
/** Chặng của intent (mặc định ⑤). */
export const intentStageOf = (r: Pick<AssistantRequest, 'stageKey'>): 'script' | 'skeleton' | 'split' | 'source' | 'localize' => r.stageKey ?? 'script';
export type AssistantIntent = AssistantRequest & { nonce: number };

/** 261015 P5a (D1): nhãn có tiền ⇒ cú bấm CHÍNH LÀ xác nhận chi (khuôn "Phương án (tốn AI)"). */
export const FIX_LABEL = 'Nhờ AI sửa (tốn AI)';
/** 261015 P5a (D1/G6): câu gửi cố định của chip sửa lỗi — không dùng `suggestedText` (câu hỏi "Giải thích…" khiến AI trả lời chay, tốn tiền mà không có khối). */
export const FIX_REQUEST_TEXT = 'Sửa đúng các lỗi đã tick bằng thay đổi nhỏ nhất; không đổi phần khác.';

export interface AssistAction { label: typeof FIX_LABEL | 'Hỏi trợ lý'; intent: AssistantRequest; disabledReason?: string }

const MAX_QUOTE = 300;
const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);

/** Mã chặn sửa của MỘT hàng tập (cùng luật `episodeRowFixBlocker`, trả mã để dùng chung `editBlockerText`). */
export const rowEditBlockerCode = (sc: Pick<EpisodeScriptRecord, 'status' | 'script'> | undefined | null): string | null => {
  if (!sc || sc.status !== 'DRAFT' || !sc.script) return 'NOT_DRAFT';
  const scenes = Array.isArray(sc.script.scenes) ? sc.script.scenes : [];
  if (!scenes.length || !Array.isArray(sc.script.scenePlan) || !sc.script.scenePlan.length) return 'SCENE_PLAN_MISSING';
  if (scenes.some((s) => !String(s?.scenePlanId ?? '').trim())) return 'SCENE_IDS_MISSING';
  return null;
};

/** Dòng "Cần xem" có nút gì: có id ⇒ "Nhờ AI sửa (tốn AI)" (gửi luôn); không ⇒ "Hỏi trợ lý"; failed/continuity/reader_summary/orphan ⇒ không nút (User chốt). */
export const reasonAssistAction = (reason: ReviewReason, ep: number, editBlocker: string | null, locked = false): AssistAction | null => {
  if (reason.kind !== 'critic' && reason.kind !== 'reader') return null;
  const suggestedText = `Tập ${ep} có lỗi: "${clip(String(reason.text ?? '').trim(), MAX_QUOTE)}". Giải thích ngắn vì sao và nên sửa ở cảnh nào.`;
  const hasId = !!reason.issueId;
  const disabledReason = editBlocker ? (editBlockerText(editBlocker) ?? 'Tập này không sửa được') : locked ? 'Bước đang khoá' : undefined;
  return {
    label: hasId ? FIX_LABEL : 'Hỏi trợ lý',
    intent: { episode: ep, issueIds: hasId ? [reason.issueId as string] : [], suggestedText, ...(hasId ? { autoSend: 'fix' as const } : {}) },
    ...(disabledReason ? { disabledReason } : {}),
  };
};

export interface IntentDraft { text: string; selected: string[] }

/** Áp ý định lên nháp (khung đã ở đúng tập, chip đã tải). Không bỏ tick cũ; không ghi đè chữ đang gõ. */
export const applyAssistantIntent = (draft: IntentDraft, intent: AssistantRequest, chipIds: readonly string[]): { draft: IntentDraft; notice?: string } => {
  const ok = intent.issueIds.filter((id) => chipIds.includes(id));
  const missing = intent.issueIds.filter((id) => !chipIds.includes(id));
  const selected = [...draft.selected, ...ok.filter((id) => !draft.selected.includes(id))];
  const hasText = draft.text.trim() !== '';
  const wantsText = !!intent.suggestedText && (intent.issueIds.length === 0 || missing.length > 0);
  if (wantsText) {
    if (!hasText) {
      return { draft: { text: intent.suggestedText as string, selected }, ...(missing.length ? { notice: 'Lỗi này không còn trong danh sách lỗi của tập — đã điền câu hỏi thay vì tick.' } : {}) };
    }
    return {
      draft: { text: draft.text, selected },
      notice: missing.length
        ? 'Lỗi này không còn trong danh sách lỗi của tập — ô soạn đang có nội dung nên chưa điền câu gợi ý.'
        : 'Ô soạn đang có nội dung — chưa điền câu gợi ý.',
    };
  }
  if (ok.length && hasText) return { draft: { text: draft.text, selected }, notice: 'Ô soạn đang có nội dung — giữ nguyên, chỉ tick thêm lỗi.' };
  return { draft: { text: draft.text, selected } };
};

/**
 * Khi nào khung xử lý intent: `skip` (đã xử lý nonce, hoặc tập không nháp mà intent cần ô soạn), `wait` (scope/view chưa phải của tập intent —
 * so `view.head.episode`, KHÔNG chỉ "view có chưa": hook giữ view tập cũ thêm một nhịp, B1), `apply`.
 */
export const shouldApplyIntent = (
  intent: AssistantIntent, handledNonce: number | null, scope: AssistantScope, view: Pick<ScopeView, 'head' | 'editBlocker'> | null, loading: boolean,
): 'apply' | 'wait' | 'skip' => {
  if (intent.nonce === handledNonce) return 'skip';
  if (!scope.supported || scope.stageKey !== intentStageOf(intent)) return 'wait';
  if (scope.stageKey === 'skeleton' || scope.stageKey === 'split' || scope.stageKey === 'source' || scope.stageKey === 'localize') {   // cả truyện: không có tập đích
    if (loading || !view) return 'wait';
    return 'apply';
  }
  if (scope.episode !== intent.episode) return 'wait';
  if (loading || !view || view.head.episode !== intent.episode) return 'wait';
  if (view.editBlocker && !intent.openVersions) return 'skip';
  return 'apply';
};

/** Số id của intent còn trong danh sách chip (chip đã mất giữa chừng ⇒ không tự gửi `fix`). */
export const resolvedIssueCount = (intent: Pick<AssistantRequest, 'issueIds'>, chipIds: readonly string[]): number => intent.issueIds.filter((id) => chipIds.includes(id)).length;

/** Nháp để GỬI của chip `fix`: câu cố định + các lỗi đã tick (`applyAssistantIntent` để ô trống khi mọi id còn chip). */
export const fixDraftOf = (applied: IntentDraft): IntentDraft => ({ text: FIX_REQUEST_TEXT, selected: applied.selected });

/**
 * 261005 E3-P4b1 (B4 + D6) / 261015 P5a (D1) — có TỰ GỬI ("3 phương án" hoặc "Nhờ AI sửa") không. Đúng MỘT lần mỗi nonce (`sentNonce`, không gửi lại khi render/poll).
 * `draftHadText` = ô soạn ĐÃ có chữ TRƯỚC khi áp intent ⇒ KHÔNG gửi (tránh gửi nhầm câu gõ dở); `blockedReason` = composer đang tắt (lượt chạy…) ⇒ không gửi, để nháp + nói lý do.
 * `fix` còn thêm: ô soạn đang có lỗi đã tick (`draftHadChips`) ⇒ không gửi — nếu không, cú bấm một bản sửa sẽ lặng lẽ trả tiền cho cả lỗi khác (khuôn `onRetry`);
 * không id nào còn chip (`resolvedCount===0`) ⇒ không gửi (BE sẽ hạ về `free`, tự áp vô nghĩa) — `applyAssistantIntent` đã điền câu hỏi + báo.
 */
export const autoSendDecision = (o: {
  intent: Pick<AssistantIntent, 'autoSend' | 'nonce'>; verdict: 'apply' | 'wait' | 'skip'; sentNonce: number | null; draftHadText: boolean; blockedReason: string | null;
  draftHadChips?: boolean; resolvedCount?: number;
}): { send: boolean; notice?: string } => {
  const mode = o.intent.autoSend;
  if ((mode !== 'options' && mode !== 'fix') || o.verdict !== 'apply' || o.sentNonce === o.intent.nonce) return { send: false };
  if (mode === 'fix') {
    if ((o.resolvedCount ?? 0) === 0) return { send: false };
    if (o.draftHadText) return { send: false, notice: 'Ô soạn đang có nội dung — chưa tự gửi nhờ AI sửa. Gửi hoặc xoá nội dung đó rồi bấm "Nhờ AI sửa" lại.' };
    if (o.draftHadChips) return { send: false, notice: 'Ô soạn đang có lỗi đã tick — chưa tự gửi (tránh tốn AI cho cả lỗi khác). Gửi hoặc bỏ tick rồi bấm "Nhờ AI sửa" lại.' };
    if (o.blockedReason) return { send: false, notice: `Chưa tự gửi: ${o.blockedReason} Đã chuẩn bị sẵn — bấm "Nhờ AI sửa" lại khi xong.` };
    return { send: true };
  }
  if (o.draftHadText) return { send: false, notice: 'Ô soạn đang có nội dung — chưa tự gửi. Bấm "3 phương án" khi sẵn sàng.' };
  if (o.blockedReason) return { send: false, notice: `Chưa tự gửi: ${o.blockedReason} Đã chuẩn bị sẵn — bấm "3 phương án" khi xong.` };
  return { send: true };
};
